<template>
    <section v-if="heroContent" :class="{'pinned-content-hero': true, 'multiple-content': slice.variation === 'multipleContent'}">
        <div class="content">
            <div class="main-article">
                <PrismicImage
                    v-if="heroContent.type !== 'collection' && heroContent.data?.thumbnail"
                    :class="{thumbnail: true, cropped: heroContent.type === 'article'}"
                    :field="heroContent.data?.thumbnail"
                />
                <div class="content-info">
                    <h1 class="title">{{ heroContent.data?.title }}</h1>
                    <div v-if="heroContent.type !== 'guide' && heroContent.type !== 'collection' && slice.variation === 'default'" class="time-info">
                        <template v-if="hasProperty(heroContent.data, 'releaseDate') && heroContent.data?.releaseDate">
                            <Calendar class="icon" />
                            <span>
                                {{ formatDate(heroContent.data?.releaseDate, {year: 'numeric', month: 'long', day: 'numeric'}) }}
                            </span>
                        </template>
                        <template v-if="hasProperty(heroContent.data, 'duration') && heroContent.data.duration !== ''">
                            <div class="dot" />
                            <Clock class="icon" />
                            <span>{{ heroContent.data?.duration }}</span>
                        </template>
                    </div>
                    <PrismicRichText class="excerpt" :field="heroContent.data?.excerpt" />

                    <NuxtLink class="link" :to="linkResolver(heroContent)">
                        {{ t(getCtaTranslationKey(heroContent.type)) }}
                    </NuxtLink>
                </div>
            </div>
            <template v-if="slice.variation === 'multipleContent'">
                <div v-if="slice.variation === 'multipleContent'" class="side-column">
                    <PrismicRichText :field="slice.primary.sideContentTitle" class="side-column-title" />
                    <NuxtLink v-for="item in resolvedSideContents" :key="item.id" :to="linkResolver(item)" class="side-content">
                        <div>
                            <h3 class="side-title">{{ item.data?.title }}</h3>
                            <div v-if="item.type !== 'guide' && item.type !== 'collection' && item.date" class="time-info">
                                <Calendar class="icon" />
                                <span>
                                    {{ formatDate(item.date, {year: 'numeric', month: 'long', day: 'numeric'}) }}
                                </span>
                            </div>
                        </div>
                        <VJoyButton variant="secondary" icon="arrow-right" class="side-button" />
                    </NuxtLink>
                </div>
            </template>
        </div>
    </section>
</template>

<script lang="ts" setup>
    import {inject, computed, ref} from 'vue';
    import type {PrismicDocument} from '@prismicio/types';
    import {getSliceComponentProps, PrismicImage, PrismicRichText} from '@prismicio/vue';
    import type {PinnedContentHeroSlice} from '@prismicio-types';
    import {useRoute} from '@typed-router';
    import {usePrismicTypeGuards, useTranslation, useAsyncData, createContentHelpers, useFormat} from '#imports';
    import {VJoyButton} from '@maltjoy/core-vue';
    import {PRISMIC_DOCUMENT} from '~/app/keys';
    import linkResolver from '~/app/prismic/link-resolver';
    import Calendar from '~/components/icons/Calendar.vue';
    import Clock from '~/components/icons/Clock.vue';
    import {fetchFallbackDocuments, resolveDocType} from './resolvePinnedHeroFallback';
    import type {TranslationKey} from '#build/i18n';
    import type {FilledContent} from '~~/app/composables/contentHelpers';

    const props = defineProps({...getSliceComponentProps<PinnedContentHeroSlice>()});
    const {hasProperty} = usePrismicTypeGuards();

    const {t} = useTranslation();
    const {formatDate} = useFormat();

    function getCtaTranslationKey(type: string): TranslationKey {
        switch (type) {
            case 'successStory':
                return `resources.successStories.readLink`;
            case 'collection':
                return `resources.collection`;
            case 'event':
                return `resources.events.signup`;
            case 'trends':
                return `resources.trends.readLink`;
            default:
                return `resources.${type}s.readLink` as TranslationKey;
        }
    }

    function getDateForDisplay(item: FilledContent): Date | undefined {
        if (item.type === 'event' && item.data?.date) {
            return new Date(item.data.date);
        }
        if ((item.type === 'article' || item.type === 'guide' || item.type === 'successStory') && item.data?.releaseDate) {
            return new Date(item.data.releaseDate);
        }
        return undefined;
    }

    const {heroContent, resolvedSideContents} = await usePinnedContentHero(props);

    async function usePinnedContentHero(props: Readonly<{slice: PinnedContentHeroSlice}>) {
        const route = useRoute();
        const prismicDocument = inject(PRISMIC_DOCUMENT, ref<PrismicDocument | undefined>());
        const {isFilledContentRelationship} = createContentHelpers();

        const MULTIPLE_CONTENT_SIDE_COUNT = 3;
        const docType = resolveDocType(prismicDocument.value, route.name);
        const key = `pinned-content-hero-fallback:${docType}:${props.slice.variation}`;

        const main = ref(props.slice.primary.mainContent);
        const hasMain = isFilledContentRelationship(main.value) && !main.value.isBroken;

        const pinnedHasContent = computed(() => {
            if (props.slice.variation === 'multipleContent') {
                return hasMain || props.slice.items.some((row) => isFilledContentRelationship(row.sideContent));
            }
            return hasMain;
        });

        const totalSlotCount = computed(() => (props.slice.variation === 'multipleContent' ? 1 + MULTIPLE_CONTENT_SIDE_COUNT : 1));

        const {data: fallbackDocuments} = await useAsyncData(key, async () => {
            if (pinnedHasContent.value || !docType) return [];
            return fetchFallbackDocuments(docType, totalSlotCount.value)();
        });

        const pinnedMultipleContent = computed(() => {
            if (hasMain) return props.slice.primary.mainContent as FilledContent;
            return props.slice.items.map((row) => row.sideContent).find((c): c is FilledContent => isFilledContentRelationship(c) && !c.isBroken);
        });

        const heroContent = computed(() => {
            if (pinnedHasContent.value) {
                if (props.slice.variation === 'multipleContent') {
                    return pinnedMultipleContent.value ?? null;
                }
                return props.slice.primary.mainContent as FilledContent;
            }

            return fallbackDocuments.value?.[0] as unknown as FilledContent;
        });

        function toFilledContentWithDate(items: FilledContent[]) {
            return items.map((item) => ({...item, date: getDateForDisplay(item)}));
        }

        const pinnedSideContents = computed(() => {
            const filledSides = props.slice.items
                .map((item) => item.sideContent)
                .filter((item) => isFilledContentRelationship(item) && !item.isBroken) as FilledContent[];

            return hasMain ? filledSides : filledSides.slice(1);
        });

        const fallbackSideContents = computed((): FilledContent[] => {
            const docs = fallbackDocuments.value;
            if (!docs || docs.length <= 1) return [];

            return docs.slice(1, 1 + MULTIPLE_CONTENT_SIDE_COUNT).map((doc) => doc as unknown as FilledContent);
        });

        const resolvedSideContents = computed(() => {
            const items = pinnedHasContent.value ? pinnedSideContents.value : fallbackSideContents.value;
            return toFilledContentWithDate(items);
        });

        return {
            heroContent,
            resolvedSideContents,
        };
    }
</script>

<style lang="scss">
    @use '~/assets/styles/variables' as *;
    @use '@malt/joy-entrypoint/src/sass/base/2_mixins/media-queries/media-queries' as mq;

    .pinned-content-hero {
        background-color: var(--joy-color-quaternary-10);
        padding: 60px 0 90px;

        @include mq.screen_sm(min) {
            padding: 110px 0 140px;
        }

        @include mq.screen_xlg(min) {
            &::before {
                content: '';
                position: absolute;
                top: 0;
                left: 0;
                width: 100%;
                height: 70%;
                pointer-events: none;
                background-image: url('~/assets/svg/heroPills.svg');
                background-position: top left;
                background-repeat: no-repeat;
                background-size: auto 100%;
            }
        }

        .content {
            position: relative;
            z-index: 1;
            @include content-width;

            .main-article {
                display: flex;
                flex-direction: column;

                @include mq.screen_sm(min) {
                    display: grid;
                    align-items: center;
                    grid-template-columns: 1fr 1fr;
                    column-gap: var(--joy-core-spacing-9);
                }

                @include mq.screen_md(min) {
                    column-gap: var(--joy-core-spacing-12);
                }

                @include mq.screen_lg(min) {
                    column-gap: 150px;
                }
            }

            .category-pill a:not(:hover)::after {
                background-color: var(--joy-color-quaternary-10);
            }

            .thumbnail {
                width: 100%;
                object-fit: contain;
                object-position: center;
                border-radius: var(--joy-core-radius-4);

                &.cropped {
                    object-fit: cover;
                }

                @media (max-width: $mobile-breakpoint) {
                    height: auto;
                    width: 100%;
                    max-height: 350px;
                    margin-bottom: var(--joy-core-spacing-4);
                }
            }

            .title {
                margin-top: var(--joy-core-spacing-4);
                margin-bottom: var(--joy-core-spacing-8);

                @include mq.screen_sm(min) {
                    margin-bottom: 0;
                }
            }

            .time-info {
                display: flex;
                align-items: center;
                color: var(--joy-color-neutral-40);

                .dot {
                    height: 4px;
                    width: 4px;
                    background-color: var(--joy-color-neutral-60);
                    border-radius: var(--joy-core-radius-6);
                    margin: 0px var(--joy-core-spacing-4);
                }

                .icon {
                    margin-right: var(--joy-core-spacing-2);
                }
            }

            .excerpt {
                margin-top: var(--joy-core-spacing-5);

                p {
                    color: var(--joy-color-neutral-40);
                }
            }

            .tags {
                margin-top: var(--joy-core-spacing-5);
                display: flex;
                gap: var(--joy-core-spacing-1);
                flex-wrap: wrap;
            }

            .link {
                @include link;
                display: inline-block;
                margin-top: var(--joy-core-spacing-5);
            }
        }

        &.multiple-content {
            padding: var(--joy-core-spacing-9) 0 80px;
            @media (max-width: $mobile-breakpoint) {
                padding: var(--joy-core-spacing-5) 0 var(--joy-core-spacing-9);
            }

            .content {
                display: flex;
                flex-direction: column;
                gap: var(--joy-core-spacing-11);

                @include mq.screen_sm(min) {
                    display: grid;
                    grid-template-columns: 1.2fr 1fr;
                }

                @include mq.screen_md(min) {
                    column-gap: var(--joy-core-spacing-12);
                }

                @include mq.screen_lg(min) {
                    column-gap: 150px;
                }

                .thumbnail {
                    height: 300px;
                    object-position: left;
                    border-radius: var(--joy-core-radius-4);

                    @media (max-width: $mobile-breakpoint) {
                        height: auto;
                        max-height: 330px;
                    }
                }

                .main-article {
                    display: flex;
                    flex-direction: column;

                    h1 {
                        font-size: 40px;
                        @media (max-width: $mobile-breakpoint) {
                            font-size: var(--joy-font-size-secondary-200);
                            margin-bottom: var(--joy-core-spacing-5);
                        }
                    }

                    .content-info {
                        margin-top: var(--joy-core-spacing-5);
                    }

                    .link {
                        @media (max-width: $mobile-breakpoint) {
                            display: inline-block;
                        }
                    }
                }

                .side-column {
                    display: flex;
                    flex-direction: column;
                    align-items: flex-start;
                    align-self: stretch;

                    .side-column-title h2 {
                        color: var(--joy-color-primary-50);
                        font-family: var(--joy-font-family-title);
                        font-size: var(--joy-font-size-secondary-200);
                        font-weight: var(--joy-font-weight-normal);
                        padding-bottom: var(--joy-core-spacing-4);
                    }
                }

                .side-content {
                    display: flex;
                    align-items: center;
                    gap: var(--joy-core-spacing-12);
                    padding: var(--joy-core-spacing-7) 0;
                    border-bottom: solid 1px var(--joy-color-neutral-30);
                    width: 100%;
                    justify-content: space-between;

                    &:first-of-type {
                        padding-top: 0;
                    }

                    .time-info {
                        margin-top: var(--joy-core-spacing-3);
                    }

                    .side-button {
                        --button-color-secondary: var(--joy-color-primary-50);
                        --button-bg-color-secondary-hover: var(--joy-color-primary-10);
                        padding: 0 10px;

                        .joy-icon {
                            margin-right: 0px;
                        }
                    }

                    &:last-child {
                        border-bottom: none;
                    }
                }
            }
        }
    }
</style>
